/**
 * Parse slot types from a framework template
 * Example: "This is [adjective] and [noun]" -> [{ slotType: "adjective", index: 0 }, { slotType: "noun", index: 1 }]
 */
export function parseSlots(template) {
    const slotRegex = /\[(\w+)\]/g;
    const slots = [];
    const errors = [];
    let match;
    let index = 0;
    while ((match = slotRegex.exec(template)) !== null) {
        const slotType = match[1];
        slots.push({ slotType, index });
        index++;
    }
    // Validate template has at least one slot
    if (slots.length === 0) {
        errors.push({
            field: 'template',
            message: 'Template must contain at least one slot in format [slotType]',
            severity: 'error',
        });
    }
    // Check for malformed brackets
    const unmatchedOpen = (template.match(/\[(?!\w+\])/g) || []).length;
    const unmatchedClose = (template.match(/(?<!\[\w+)\]/g) || []).length;
    if (unmatchedOpen > 0 || unmatchedClose > 0) {
        errors.push({
            field: 'template',
            message: 'Template contains malformed brackets. Use format [slotType]',
            severity: 'error',
        });
    }
    return { slots, errors };
}
/**
 * Validate slot types exist in available word banks
 * This validation is optional as it requires runtime knowledge of available word banks
 */
export function validateSlotTypes(slots, availableSlotTypes) {
    const errors = [];
    const unknownTypes = new Set();
    for (const slot of slots) {
        if (!availableSlotTypes.includes(slot.slotType)) {
            unknownTypes.add(slot.slotType);
        }
    }
    for (const slotType of unknownTypes) {
        errors.push({
            field: 'template',
            message: `Unknown slot type: [${slotType}]. No wordbank exists for this type.`,
            severity: 'error',
        });
    }
    return errors;
}
/**
 * Validate a framework object
 * @param framework The framework to validate
 * @param category The category it belongs to
 * @param availableSlotTypes Optional list of available slot types for validation
 */
export function validateFramework(framework, category, availableSlotTypes) {
    const errors = [];
    // Required field: id
    if (!framework.id || !framework.id.trim()) {
        errors.push({
            field: 'id',
            message: 'ID is required',
            severity: 'error',
        });
    }
    // Required field: template
    if (!framework.template || !framework.template.trim()) {
        errors.push({
            field: 'template',
            message: 'Template is required',
            severity: 'error',
        });
    }
    // ID format validation (lowercase alphanumeric with underscores)
    if (framework.id && !/^[a-z0-9_]+$/.test(framework.id)) {
        errors.push({
            field: 'id',
            message: 'ID must be lowercase alphanumeric with underscores only (a-z, 0-9, _)',
            severity: 'error',
        });
    }
    // ID should follow convention: start with category prefix
    if (framework.id && !framework.id.startsWith(category)) {
        errors.push({
            field: 'id',
            message: `ID should start with category prefix "${category}_" (e.g., "${category}_opener_1")`,
            severity: 'warning',
        });
    }
    // Marker validation
    const validMarkers = ['', 'opener', 'first'];
    if (framework.marker !== undefined && !validMarkers.includes(framework.marker)) {
        errors.push({
            field: 'marker',
            message: `Marker must be one of: ${validMarkers.map(m => m === '' ? '(empty for random)' : m).join(', ')}`,
            severity: 'error',
        });
    }
    // Template validation
    if (framework.template) {
        const { slots, errors: parseErrors } = parseSlots(framework.template);
        errors.push(...parseErrors);
        // Validate slot types if available
        if (availableSlotTypes && slots.length > 0) {
            const slotTypeErrors = validateSlotTypes(slots, availableSlotTypes);
            errors.push(...slotTypeErrors);
        }
    }
    return errors;
}
/**
 * Get a summary of slot types used in a template
 * Useful for displaying validation feedback
 */
export function getSlotTypeSummary(template) {
    const { slots } = parseSlots(template);
    if (slots.length === 0) {
        return 'No slots found';
    }
    const uniqueTypes = new Set(slots.map(s => s.slotType));
    return `Uses ${slots.length} slot(s): ${Array.from(uniqueTypes).join(', ')}`;
}
//# sourceMappingURL=frameworkValidator.js.map